/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataModelModule
 */

import { isUndefined } from 'underscore';
import { IIpAddrPrefix } from 'generated-types';

import { IpAddrConfigItem } from './ip-addr.config-item.factory';
import { MessageItem } from './message-item.factory';

interface IIpAddrPrefixConfig extends IIpAddrPrefix {
    ip_addr: IpAddrConfigItem;
}

/**
 * @description IpAddrPrefix MessageItem.
 *
 * @author Alex Tseung, Aravindh Nagarajan
 */
export class IpAddrPrefixConfigItem extends MessageItem<IIpAddrPrefixConfig> {
    /**
     * Used to store the state of the user-editable subnet string. This is needed as opposed to
     * using only a getter/setter to track the slash that the user types, as the slash isn't part of
     * the data model.
     */
    private viewValue: string;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'IpAddrPrefix',
            ...args,
        };

        super(extendedArgs);

        this.viewValue = this.subnet;
    }

    /**
     * @override
     */
    public canFlatten(): boolean {
        return this.isValid();
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IIpAddrPrefixConfig> {
        return {
            ip_addr: null,
        };
    }

    /**
     * Returns true if the fields are populated.
     */
    public isValid(): boolean {
        return Boolean(this.address) && !isUndefined(this.mask);
    }

    /**
     * Returns a string representation of the subnet address and mask. If the mask property
     * exists in this.data.config, we output a slash.
     */
    public get subnet(): string | undefined {
        const { address, mask } = this;
        let suffix;

        if (mask === 0 || mask) {
            suffix = `/${mask}`;
        } else {
            suffix = '';
        }

        return address && address + suffix || undefined;
    }

    /**
     * Sets the address and mask properties in the config. If the subnetString contains a slash,
     * we populate mask with either an empty string or the mask number. Otherwise, we delete the
     * mask property. The presence of the mask property helps us determine whehter or not we
     * should show a slash in the output string.
     */
    public set subnet(subnetString: string) {
        let address;
        let mask = NaN;

        if (typeof subnetString === 'string') {
            const subnetParts = subnetString.split('/');

            if (subnetParts.length === 2 && subnetParts[1]) {
                [address] = subnetParts;
                mask = Number(subnetParts[1]);
            }
        }

        this.address = address;
        this.mask = mask;
    }

    /**
     * Sets the address property in the IpAddrConfigItem config.
     */
    public set address(address: string) {
        const { ip_addr: ipAddr } = this.config;

        ipAddr.address = address;
    }

    /**
     * Returns the address of the IpAddrConfigItem config.
     */
    public get address(): string {
        const { ip_addr: ipAddr } = this.config;

        return ipAddr.address;
    }

    /**
     * Sets the mask property in the config.
     */
    public set mask(mask: number) {
        this.config.mask = mask;
    }

    /**
     * Returns the mask property from the config.
     */
    public get mask(): number {
        return this.config.mask;
    }

    /**
     * Returns a string representation of the config data.
     */
    public toString(): string {
        const { ip_addr: ipAddr } = this.config;

        return `${ipAddr}/${this.mask}`;
    }

    /** @override */
    public modifyConfigDataAfterLoad(): void {
        this.viewValue = this.subnet;
    }

    /**
     * Return the user-editable viewValue string. Use for ngModels in inputs.
     */
    public get formValue(): string {
        return this.viewValue;
    }

    /**
     * Set the viewValue and data model. Use for ngModels in inputs.
     */
    public set formValue(subnetString: string | undefined) {
        this.viewValue = subnetString;
        this.subnet = subnetString;
    }

    /**
     * Clear the configured subnet.
     */
    public clearSubnet(): void {
        this.formValue = undefined;
    }
}
